import * as yup from 'yup';
import { ArrowLeft, ArrowUpRight, Check, User } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';
import { useContext, useState } from 'react';
import { useForm } from 'react-form-state-manager';

import { AssignmentType, ParticipantAttribute, TeamInput } from '__generated__/graphql';

import { EmbedContext } from '../Common/EmbedProvider';
import {
  Event, ParticipantField, Promotion, Ticket, TimeSlot, getRequiredParticipantAttributes, getTeamFlags,
} from './helpers';
import { SectionTitle, TitleBar } from '../Common/Layout';
import { emailRegex, encodeUrl, filterPrefix, getServerErrors, mergeUrlParams } from '../../common/helpers';
import { getParticipantAttributesSchema } from '../../common/ParticipantAttributes/helpers';
import {
  getParticipantFieldEntrySchema, getPromotionFields, prefillFieldEntries,
} from '../../common/ParticipantFields/helpers';
import { prefillRegistrationCountry } from './StateProvider';
import { useEvent } from '../Common/EventProvider';
import AssignmentForm from '../../common/Registrations/AssignmentForm';
import InvitationCodeForm from './InvitationCodeForm';
import PreregisterMutation from './PreregisterMutation';
import StateContext from './StateContext';
import TeamPicker from '../../common/Teams/TeamPicker';
import UI from '../../common/UI';
import config from '../config';
import useDocumentTitle from '../../common/useDocumentTitle';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';
import useTimeSlotFormatter from '../../common/Tickets/useTimeSlotFormatter';
import useUrlState from '../../common/useUrlState';
import useValidation from '../../common/useValidation';

interface PreregistrationPageProps {
  ticketId: string;
  promotionId: string;
}

const PreregistrationPage = ({ ticketId, promotionId }: PreregistrationPageProps) => {
  const { t } = useTranslation();
  const { event } = useEvent();

  useDocumentTitle([t('preregistration.preregistration'), event.title]);

  const ticket = event.ticket_categories
    .map((ticketCategory) => ticketCategory.tickets_for_sale.find((ticket) => ticket.id === ticketId))
    .find((ticket) => ticket);

  const promotion = ticket.promotions_for_sale
    .find((promotion) => promotion.id === promotionId);

  const [urlState] = useUrlState({
    parse: (urlState, { string }) => ({
      ...urlState,
      return: string(urlState.return),
      timeSlotId: string(urlState.timeSlot),
    }),
  });

  const ticketCategory = event.ticket_categories.find(
    (ticketCategory) => ticketCategory.tickets_for_sale.map((ticket) => ticket.id).includes(ticket?.id),
  );

  const timeSlot = urlState.timeSlotId ? ticket.upcoming_time_slots.find(
    (timeSlot) => timeSlot.id === urlState.timeSlotId,
  ) : null;

  const returnLink = urlState.return === 'resale'
    ? `/${event.id}/resale?ticket=${ticket?.id}`
    : `/${event.id}${mergeUrlParams({ showCategories: [ticketCategory?.id] })}`;

  const [email, setEmail] = useState<string>();
  const success = !!email;

  return (
    <>
      <UI.Div>
        <TitleBar>
          <UI.Div>
            <UI.FadeIn>
              <UI.Link to={returnLink} title={t('back')}>
                <UI.Icon>
                  <ArrowLeft />
                </UI.Icon>
              </UI.Link>
            </UI.FadeIn>
          </UI.Div>
          <UI.Div>
            {t('preregistration.preregistration')}
          </UI.Div>
          <UI.Div />
        </TitleBar>

        <UI.Card sc={{ hasPanels: true, active: true }}>
          {success && (
            <UI.FadeIn>
              <UI.FormGrid sc={{ textAlign: 'center' }}>
                <UI.Badge sc={{ size: 48, brand: 'success' }} style={{ margin: '0 auto' }}>
                  <UI.Icon sc={{ size: 2.5 }}>
                    <Check />
                  </UI.Icon>
                </UI.Badge>

                <SectionTitle>
                  {t('preregistration.preregistered_success')}
                </SectionTitle>

                <UI.FlexContainer sc={{ justifyContent: 'center', spaceX: 1 }}>
                  <UI.LinkButton to={`/${event.id}`} sc={{ outline: true }}>
                    <UI.Icon>
                      <ArrowLeft />
                    </UI.Icon>
                    {' '}
                    {t('back')}
                  </UI.LinkButton>
                  {' '}
                  <UI.AButton
                    href={`/login?${encodeUrl({ email })}`}
                    target="_blank"
                    rel="noreferer noopener"
                    sc={{ outline: true }}
                  >
                    <UI.Icon>
                      <User />
                    </UI.Icon>
                    {' '}
                    {t('success_page.go_to_dashboard')}
                  </UI.AButton>
                </UI.FlexContainer>
              </UI.FormGrid>
            </UI.FadeIn>
          )}
          {ticket && ticket.preregistration && promotion && !success && (
            <UI.FadeIn>
              <PreregistrationForm
                event={event}
                ticket={ticket}
                promotion={promotion}
                timeSlot={timeSlot}
                onSuccess={(email) => setEmail(email)}
              />
            </UI.FadeIn>
          )}
          {(!ticket || !ticket.preregistration || !promotion) && (
            <UI.Div sc={{ textAlign: 'center' }}>
              {t('preregistration.preregistration_not_available')}
            </UI.Div>
          )}
        </UI.Card>
      </UI.Div>

      {event.has_invitation_codes && (
        <InvitationCodeForm />
      )}
    </>
  );
};

interface PreregistrationFormProps {
  event: Event;
  ticket: Ticket;
  promotion: Promotion;
  timeSlot: TimeSlot | null;
  onSuccess: (email: string) => unknown;
}

const PreregistrationForm = ({ event, ticket, promotion, timeSlot, onSuccess }: PreregistrationFormProps) => {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { scrollToCheckoutForm } = useContext(EmbedContext);
  const formatTimeSlot = useTimeSlotFormatter();

  /** URL params on successfully placing an order. */
  const { form: { source_url: sourceUrl, initial_url: initialUrl, final_url: finalUrl } } = useContext(StateContext);

  const requireDetails = ticket.assignment_type === AssignmentType.required;

  const requiredParticipantFields = requireDetails
    ? getPromotionFields(event.enabled_participant_fields, promotion.id)
    : [];

  const { allowIndividuals, allowCreateTeam, allowJoinTeam } = getTeamFlags([ticket]);

  /** Show the team form if creating or joining teams is allowed. */
  const showTeamForm = (allowCreateTeam || allowJoinTeam) && !event.invitation_code?.invitation.team;
  const mustCreateTeam = !allowIndividuals && allowCreateTeam && !allowJoinTeam;
  const mustJoinTeam = !allowIndividuals && !allowCreateTeam && allowJoinTeam;

  const termsText = event.terms_text;
  const termsUrl = event.terms_url || event.terms?.url;
  const requireTerms = termsText || termsUrl;

  const form = useForm({
    values: {
      registration: prefillRegistrationCountry({
        ticket: {
          id: ticket.id,
        },
        team: null,
        time_slot: timeSlot ? { id: timeSlot.id } : null,
        purchase: {
          promotion: {
            id: promotion.id,
          },
        },
        participant: {
          mail_opt_ins: [],
        },
        details: {},
        fields: prefillFieldEntries([], requiredParticipantFields),
        upgrades: [],
      }, event),
      terms: false,
    },
  });

  const handleTeamChange = (team: TeamInput | null) => {
    form.set('registration.team', team);
  };

  const requiredParticipantAttributes = requireDetails
    ? getRequiredParticipantAttributes(form.values.registration, event)
    : [];

  const scrollToErrorFields = () => {
    scrollToCheckoutForm();
    Object.keys(errors).forEach((key: string) => form.setTouched(key, true));
  };

  const [preregister, { error, loading }] = useMutation(PreregisterMutation, {
    variables: {
      input: {
        registration: form.values.registration,
        source_url: sourceUrl,
        initial_url: initialUrl,
        final_url: finalUrl,
      },
    },
    onCompleted: () => {
      onSuccess(form.values.registration.participant.email);
    },
    onError: () => {
      scrollToErrorFields();
    },
  });

  const { valid, errors, updateCustomErrors, updateValidating } = useValidation({
    schema: getPreregistrationSchema(requiredParticipantFields, requiredParticipantAttributes, event),
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => preregister()} id="CheckoutForm">
      <UI.FormGrid>
        <SectionTitle>
          {t('preregistration.preregister')}
        </SectionTitle>

        <UI.Card sc={{ spacing: 'sm', outline: 'gray.200', background: 'gray.50' }}>
          <UI.H4>
            <UI.Icon>
              <UI.Icons.Ticket />
            </UI.Icon>
            {' '}
            {ticket.title}
          </UI.H4>
          {(promotion.title || timeSlot) && (
            <UI.Delimit>
              {promotion.title}
              {timeSlot && formatTimeSlot(timeSlot)}
            </UI.Delimit>
          )}
        </UI.Card>

        {error && (
          <UI.ServerErrors error={error} />
        )}

        <AssignmentForm
          registration={form.values.registration}
          onChange={(update) => form.set((values) => ({
            ...values,
            registration: {
              ...values.registration,
              ...update(values.registration),
              upgrades: values.registration.upgrades,
            },
          }))}
          onBlur={(field) => form.setTouched(`registration.${field}`, true)}
          touched={form.getTouched('registration')}
          registrationAttributes={requiredParticipantAttributes}
          errors={filterPrefix(errors, 'registration')}
          enabledParticipantFields={event.enabled_participant_fields}
          updateCustomErrors={updateCustomErrors}
          updateValidating={updateValidating}
        />

        {showTeamForm && (
          <>
            <UI.HR />
            <UI.FormGrid>
              <SectionTitle>
                {mustCreateTeam ? t('common:team_picker.create_new_team') : (
                  mustJoinTeam ? t('common:team_picker.join_existing_team') : t('team')
                )}
              </SectionTitle>
              <TeamPicker
                team={form.values.registration.team}
                eventId={event.id}
                privateTeams={event.private_teams}
                allowIndividuals={allowIndividuals}
                allowCreateTeam={allowCreateTeam}
                allowJoinTeam={allowJoinTeam}
                onChange={handleTeamChange}
                errors={errors?.registration?.team}
              />
            </UI.FormGrid>
            <UI.HR />
          </>
        )}

        {event.mail_permissions.map((mailPermission) => (
          <UI.InputGroup key={mailPermission.id}>
            <UI.InputLabel htmlFor={`MailPermission_${mailPermission.id}`}>
              {mailPermission.title}
            </UI.InputLabel>
            <UI.Checkbox
              {...form.checklist('registration.participant.mail_opt_ins', mailPermission.id)}
              id={`MailPermission_${mailPermission.id}`}
            >
              {mailPermission.label}
            </UI.Checkbox>
          </UI.InputGroup>
        ))}

        {requireTerms && (
          <UI.InputGroup id="TermsSection" sc={{ valid: !errors.terms, touched: form.getTouched('terms') }}>
            <UI.InputLabel>
              {t('terms_and_conditions')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            {termsUrl && (
              <UI.Checkbox {...form.checkbox('terms')}>
                <Trans i18nKey="agree_to_terms">
                  {/* eslint-disable */}
                  <a href={termsUrl} target="_blank" rel="noopener noreferrer" />
                  {/* eslint-enable */}
                </Trans>
              </UI.Checkbox>
            )}
            {termsText && (
              <UI.Checkbox {...form.checkbox('terms')}>
                <UI.HTML html={termsText} />
              </UI.Checkbox>
            )}
            <UI.ErrorMessages attribute={t('terms_and_conditions')} errors={errors.terms} />
          </UI.InputGroup>
        )}

        {!valid && (
          <UI.FadeIn>
            <UI.Warning>
              <UI.A
                onClick={() => scrollToErrorFields()}
                sc={{ secondary: true }}
                style={{ fontWeight: 500 }}
                role="button"
              >
                {t('fill_required_fields')}
                {' '}
                <UI.Icon>
                  <ArrowUpRight />
                </UI.Icon>
              </UI.A>
            </UI.Warning>
          </UI.FadeIn>
        )}

        <UI.HR />

        <UI.Div>
          <Trans
            i18nKey="common:terms.terms_notice"
            values={{
              action: t('preregistration.preregister'),
            }}
          >
            <UI.A href={config.termsUrl[locale]} target="_blank" rel="noopener noreferer" />
          </Trans>
        </UI.Div>

        <UI.Button
          type="submit"
          sc={{ brand: 'secondary', block: true, loading }}
          disabled={loading || !valid}
        >
          <UI.Icon>
            <Check />
          </UI.Icon>
          {' '}
          {t('preregistration.preregister')}
        </UI.Button>
      </UI.FormGrid>
    </UI.Form>
  );
};

const getPreregistrationSchema = (
  requiredFields: ParticipantField[],
  requiredAttributes: ParticipantAttribute[],
  event: Event,
) => {
  const fieldEntrySchema = getParticipantFieldEntrySchema(requiredFields);

  return yup.object({
    registration: yup.object({
      participant: yup.object({
        email: yup.string().required().matches(emailRegex, { name: 'email' }),
        first_name: yup.string().ensure().required(),
        last_name: yup.string().ensure().required(),
      }),
      details: getParticipantAttributesSchema(requiredAttributes),
      fields: yup.array(fieldEntrySchema),
    }),
    terms: yup.boolean().test('required', '', (value) => (
      event.terms || event.terms_url || event.terms_text ? value : true
    )),
  });
};

export default PreregistrationPage;
